import { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppLayout } from "../../components/layout/Header";
import { useAuth } from "../../js/hooks/useAuth";
import {
  ACCOUNT_ROLES,
  ACCOUNT_STATUSES,
  ORG_TYPES,
  PLANS,
  SUB_STATUSES,
  accountBundle,
  assignAccountOrg,
  buildCorporateReport,
  buildForecast,
  buildRevenueReport,
  buildTaxReport,
  buildUserBaseReport,
  canManageAccounts,
  changeAccountRole,
  changePlan,
  chargeOwner,
  createAccount,
  createOrganization,
  ensureAccounts,
  exportAccountsCsv,
  exportAccountsPdf,
  exportTaxCsv,
  getOrganization,
  issueCredit,
  listAccountAudit,
  listAccounts,
  listOrganizations,
  listPayments,
  listReminders,
  markRefund,
  money,
  overrideSuspension,
  planLabel,
  roleLabel,
  runDunning,
  setAccountStatus,
  setPaymentMethod,
  statusLabel,
  subscriptionFor,
  updateAccount,
  updateBillingProfile,
  updateOrganization,
  deleteAccount,
  filterAccounts,
  buildAdminOverview,
  emergencyPasswordResetForAccount,
  adminLifecycleSummary,
  adminSuspendDesk,
  adminReactivateDesk,
  captureRestorePoint,
  RESTORE_SCOPES,
} from "../../js/accounts";
import RestoreTimeline from "../../components/restore/RestoreTimeline";
import { formatDate } from "../../js/utils/formatters";
import { statusLabel as lifeStatusLabel } from "../../js/api/accountLifecycle";

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "directory", label: "Directory" },
  { id: "organizations", label: "Organizations" },
  { id: "billing", label: "Billing" },
  { id: "reports", label: "Reports" },
  { id: "forecast", label: "Forecast" },
];

const EMPTY_ACCOUNT = {
  name: "",
  email: "",
  phone: "",
  role: "agent",
  status: "active",
  organization_id: "",
  plan_id: "agent_monthly",
  city: "",
  county: "",
  state: "CA",
};

function Spark({ series, valueKey = "active" }) {
  const width = 420;
  const height = 110;
  const pad = 16;
  const peak = Math.max(1, ...series.map((row) => Number(row[valueKey] || 0)));
  const points = series.map((row, index) => {
    const x = pad + (index / Math.max(1, series.length - 1)) * (width - pad * 2);
    const y = height - pad - (Number(row[valueKey] || 0) / peak) * (height - pad * 2);
    return `${x},${y}`;
  });
  return (
    <svg className="am-chart" viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      <polyline points={points.join(" ")} />
    </svg>
  );
}

function BarList({ rows, labelKey = "id", valueKey = "count" }) {
  const peak = Math.max(1, ...rows.map((row) => Number(row[valueKey] || 0)));
  return (
    <ul className="am-bars">
      {rows.map((row) => (
        <li key={row[labelKey] || row.label}>
          <span>
            {row.label || row.name || row.id} <em>{row.pretty || row[valueKey]}</em>
          </span>
          <i style={{ width: `${Math.round((Number(row[valueKey] || 0) / peak) * 100)}%` }} />
        </li>
      ))}
    </ul>
  );
}

export default function AccountManagement() {
  const { session, role } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((item) => item.id === params.get("tab")) ? params.get("tab") : "overview";
  const filterRole = params.get("role") || "";
  const filterStatus = params.get("status") || "";
  const filterPlan = params.get("plan") || "";
  const filterQuery = params.get("q") || "";
  const [pulse, setPulse] = useState(0);
  const [notice, setNotice] = useState("");
  const [form, setForm] = useState(EMPTY_ACCOUNT);
  const [orgForm, setOrgForm] = useState({ name: "", type: "brokerage", city: "", county: "", state: "CA", seat_limit: 20 });
  const [credit, setCredit] = useState({ amount: "", note: "" });
  const [methodRef, setMethodRef] = useState("");
  const [refundNote, setRefundNote] = useState("");

  ensureAccounts();

  const accounts = useMemo(() => listAccounts(), [pulse]);
  const filteredAccounts = useMemo(
    () =>
      filterAccounts(accounts, {
        role: filterRole,
        status: filterStatus,
        planId: filterPlan,
        query: filterQuery,
      }),
    [accounts, filterPlan, filterQuery, filterRole, filterStatus]
  );
  const overview = useMemo(() => buildAdminOverview(accounts), [accounts]);
  const orgs = useMemo(() => listOrganizations(), [pulse]);
  const selected = id ? accounts.find((row) => row.id === id) || null : null;
  const bundle = selected ? accountBundle(selected.id) : null;
  const revenue = useMemo(() => buildRevenueReport(), [pulse]);
  const users = useMemo(() => buildUserBaseReport(), [pulse]);
  const tax = useMemo(() => buildTaxReport(), [pulse]);
  const corporate = useMemo(() => buildCorporateReport(), [pulse]);
  const forecast = useMemo(() => buildForecast(), [pulse]);
  const payments = useMemo(() => listPayments(), [pulse]);
  const reminders = useMemo(() => listReminders(), [pulse]);
  const auditRows = useMemo(() => listAccountAudit().slice(0, 12), [pulse]);

  function flash(message) {
    setNotice(message);
    setPulse((n) => n + 1);
  }

  function setFilters(next) {
    const merged = {
      tab: params.get("tab") || tab,
      role: filterRole,
      status: filterStatus,
      plan: filterPlan,
      q: filterQuery,
      ...next,
    };
    Object.keys(merged).forEach((key) => {
      if (!merged[key]) delete merged[key];
    });
    setParams(merged);
  }

  if (role !== "admin" || !canManageAccounts(session)) {
    return (
      <AppLayout variant="admin">
        <p>Account Management is for the program desk only.</p>
      </AppLayout>
    );
  }

  return (
    <AppLayout variant="admin">
      <div className="am">
        <header className="page-header">
          <div>
            <h1>Account Management</h1>
            <p className="page-lead">
              Onboard desks, keep billing tidy, and prepare a quiet record for the accountant. Card
              PayPal holds the card door. This desk keeps the ledger, invoices, and reminders.
            </p>
          </div>
          <div className="am-exports">
            <button type="button" className="ghost-btn" onClick={() => exportAccountsCsv()}>
              Directory CSV
            </button>
            <button type="button" className="ghost-btn" onClick={() => exportTaxCsv()}>
              Tax CSV
            </button>
            <button type="button" className="primary-btn" onClick={() => exportAccountsPdf()}>
              Accounts PDF
            </button>
          </div>
        </header>
        {notice ? <p className="confirm-banner">{notice}</p> : null}

        <nav className="am-tabs" aria-label="Account Management">
          {TABS.map((item) => (
            <button
              key={item.id}
              type="button"
              className={`am-tab${tab === item.id ? " is-on" : ""}`}
              onClick={() => navigate(`/admin/accounts?tab=${item.id}`)}
            >
              {item.label}
            </button>
          ))}
        </nav>

        {tab === "overview" && !selected ? (
          <section className="am-grid">
            <article className="desk-panel card">
              <h2>Accounts at a glance</h2>
              <p className="muted">{overview.total} desks on this device · estimated MRR {money(overview.mrr)}</p>
              <ul className="am-bars">
                {ACCOUNT_ROLES.map((item) => (
                  <li key={item.id}>
                    <span>
                      {item.label} <em>{overview.byRole[item.id] || 0}</em>
                    </span>
                    <i style={{ width: `${overview.total ? Math.round(((overview.byRole[item.id] || 0) / overview.total) * 100) : 0}%` }} />
                  </li>
                ))}
              </ul>
            </article>
            <article className="desk-panel card">
              <h2>Status</h2>
              <ul className="am-bars">
                {ACCOUNT_STATUSES.filter((item) => item.id !== "deleted").map((item) => (
                  <li key={item.id}>
                    <span>
                      {item.label} <em>{overview.byStatus[item.id] || 0}</em>
                    </span>
                    <i style={{ width: `${overview.total ? Math.round(((overview.byStatus[item.id] || 0) / overview.total) * 100) : 0}%` }} />
                  </li>
                ))}
              </ul>
              <p className="muted">{overview.suspended} suspended · billing and lifecycle controls live in each account.</p>
            </article>
            <article className="desk-panel card am-wide">
              <h2>Quick actions</h2>
              <div className="am-tools">
                <button type="button" className="primary-btn" onClick={() => navigate("/admin/accounts?tab=directory")}>
                  Open directory
                </button>
                <button type="button" className="ghost-btn" onClick={() => setFilters({ tab: "directory", status: "suspended" })}>
                  View suspended
                </button>
                <Link className="ghost-btn" to="/admin/users">
                  Users & password reset
                </Link>
                <button type="button" className="ghost-btn" onClick={() => { runDunning(session); flash("Dunning pass complete."); }}>
                  Run dunning
                </button>
              </div>
              <p className="muted">
                Unified desk: filter by role, package, and status; emergency password reset; restore points;
                plan changes; suspension; and deletion — without a separate staff console.
              </p>
            </article>
          </section>
        ) : null}

        {tab === "directory" && !selected ? (
          <div className="am-split">
            <section className="desk-panel card">
              <h2>Open a desk</h2>
              <form
                className="am-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  try {
                    const row = createAccount(session, form);
                    setForm(EMPTY_ACCOUNT);
                    flash(`Opened ${row.name}.`);
                    navigate(`/admin/accounts/${row.id}`);
                  } catch (err) {
                    flash(err.message);
                  }
                }}
              >
                <label className="field">
                  <span className="field-label">Name</span>
                  <input className="field-input" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required />
                </label>
                <label className="field">
                  <span className="field-label">Email</span>
                  <input className="field-input" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} required />
                </label>
                <label className="field">
                  <span className="field-label">Phone</span>
                  <input className="field-input" value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} />
                </label>
                <label className="field">
                  <span className="field-label">Role</span>
                  <select className="field-select" value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value })}>
                    {ACCOUNT_ROLES.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">Status</span>
                  <select className="field-select" value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value })}>
                    {ACCOUNT_STATUSES.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">Organization</span>
                  <select className="field-select" value={form.organization_id} onChange={(event) => setForm({ ...form, organization_id: event.target.value })}>
                    <option value="">None</option>
                    {orgs.map((org) => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">Plan</span>
                  <select className="field-select" value={form.plan_id} onChange={(event) => setForm({ ...form, plan_id: event.target.value })}>
                    {PLANS.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {planLabel(plan.id)}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="am-form__row">
                  <label className="field">
                    <span className="field-label">City</span>
                    <input className="field-input" value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} />
                  </label>
                  <label className="field">
                    <span className="field-label">County</span>
                    <input className="field-input" value={form.county} onChange={(event) => setForm({ ...form, county: event.target.value })} />
                  </label>
                  <label className="field">
                    <span className="field-label">State</span>
                    <input className="field-input" value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value })} />
                  </label>
                </div>
                <button className="primary-btn" type="submit">
                  Create account
                </button>
              </form>
            </section>

            <section className="desk-panel card am-wide">
              <h2>Accounts</h2>
              <div className="am-filters">
                <input
                  className="field-input"
                  placeholder="Search name or email"
                  value={filterQuery}
                  onChange={(event) => setFilters({ tab: "directory", q: event.target.value })}
                />
                <select className="field-select" value={filterRole} onChange={(event) => setFilters({ tab: "directory", role: event.target.value })}>
                  <option value="">All roles</option>
                  {ACCOUNT_ROLES.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <select className="field-select" value={filterStatus} onChange={(event) => setFilters({ tab: "directory", status: event.target.value })}>
                  <option value="">All statuses</option>
                  {ACCOUNT_STATUSES.map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <select className="field-select" value={filterPlan} onChange={(event) => setFilters({ tab: "directory", plan: event.target.value })}>
                  <option value="">All packages</option>
                  {PLANS.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {planLabel(plan.id)}
                    </option>
                  ))}
                </select>
                {(filterRole || filterStatus || filterPlan || filterQuery) ? (
                  <button type="button" className="ghost-btn" onClick={() => setFilters({ tab: "directory", role: "", status: "", plan: "", q: "" })}>
                    Clear filters
                  </button>
                ) : null}
              </div>
              <p className="muted">{filteredAccounts.length} shown</p>
              <table className="admin-table am-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Role</th>
                    <th>Status</th>
                    <th>Organization</th>
                    <th>Plan</th>
                    <th>Opened</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((row) => {
                    const org = row.organization_id ? getOrganization(row.organization_id) : null;
                    const sub = subscriptionFor(row.id) || (row.organization_id ? subscriptionFor(row.organization_id) : null);
                    return (
                      <tr key={row.id} onClick={() => navigate(`/admin/accounts/${row.id}`)}>
                        <td>
                          <strong>{row.name}</strong>
                          <span className="am-sub">{row.email}</span>
                        </td>
                        <td>{roleLabel(row.role)}</td>
                        <td>{statusLabel(ACCOUNT_STATUSES, row.status)}</td>
                        <td>{org?.name || "—"}</td>
                        <td>{sub ? planLabel(sub.plan_id) : "—"}</td>
                        <td>{formatDate(row.created_at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          </div>
        ) : null}

        {tab === "directory" && selected && bundle ? (
          <AccountDetail
            key={selected.id}
            bundle={bundle}
            orgs={orgs}
            session={session}
            refundNote={refundNote}
            setRefundNote={setRefundNote}
            credit={credit}
            setCredit={setCredit}
            methodRef={methodRef}
            setMethodRef={setMethodRef}
            onFlash={flash}
            onBack={() => navigate("/admin/accounts")}
          />
        ) : null}

        {tab === "organizations" ? (
          <div className="am-split">
            <section className="desk-panel card">
              <h2>New organization</h2>
              <form
                className="am-form"
                onSubmit={(event) => {
                  event.preventDefault();
                  try {
                    createOrganization(session, orgForm);
                    setOrgForm({ name: "", type: "brokerage", city: "", county: "", state: "CA", seat_limit: 20 });
                    flash("Organization added.");
                  } catch (err) {
                    flash(err.message);
                  }
                }}
              >
                <label className="field">
                  <span className="field-label">Name</span>
                  <input className="field-input" value={orgForm.name} onChange={(event) => setOrgForm({ ...orgForm, name: event.target.value })} required />
                </label>
                <label className="field">
                  <span className="field-label">Type</span>
                  <select className="field-select" value={orgForm.type} onChange={(event) => setOrgForm({ ...orgForm, type: event.target.value })}>
                    {ORG_TYPES.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="field">
                  <span className="field-label">Seat limit</span>
                  <input
                    className="field-input"
                    type="number"
                    min="0"
                    value={orgForm.seat_limit}
                    onChange={(event) => setOrgForm({ ...orgForm, seat_limit: event.target.value })}
                  />
                </label>
                <button className="primary-btn" type="submit">
                  Add organization
                </button>
              </form>
            </section>
            <section className="desk-panel card am-wide">
              <h2>Brokerages and corporate desks</h2>
              <ul className="am-org-list">
                {corporate.map((row) => (
                  <li key={row.org.id}>
                    <div>
                      <strong>{row.org.name}</strong>
                      <p className="muted">
                        {row.org.type} · {row.org.city}, {row.org.state} · {row.seats.agents} agent
                        {row.seats.agents === 1 ? "" : "s"}
                        {row.seats.limit ? ` of ${row.seats.limit}` : ""} · {row.plan ? planLabel(row.plan.id) : "No plan"} ·{" "}
                        {money(row.collected)} collected
                      </p>
                    </div>
                    <label className="field">
                      <span className="field-label">Seat limit</span>
                      <input
                        className="field-input"
                        type="number"
                        defaultValue={row.org.seat_limit}
                        onBlur={(event) => {
                          updateOrganization(session, row.org.id, { seat_limit: Number(event.target.value || 0) });
                          flash("Seat limit saved.");
                        }}
                      />
                    </label>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        ) : null}

        {tab === "billing" ? (
          <div className="am-stack">
            <section className="desk-panel card">
              <h2>Ledger</h2>
              <p className="muted">Successful charges, declines, and refunds. Gateway references stay on the subscription.</p>
              <p>
                <button
                  type="button"
                  className="ghost-btn"
                  onClick={() => {
                    const result = runDunning(session);
                    flash(result.moved ? `Dunning advanced ${result.moved} subscription${result.moved === 1 ? "" : "s"}.` : "No subscriptions needed a nudge.");
                  }}
                >
                  Run dunning pass
                </button>
              </p>
              <table className="admin-table am-table">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Result</th>
                    <th>Amount</th>
                    <th>Reference</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {payments.map((row) => (
                    <tr key={row.id}>
                      <td>{formatDate(row.at)}</td>
                      <td>{row.result}</td>
                      <td>{money(row.amount)}</td>
                      <td>{row.method_ref}</td>
                      <td>{row.note || row.refund_note || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <section className="desk-panel card">
              <h2>Gentle reminders</h2>
              <ul className="am-notes">
                {reminders.map((row) => (
                  <li key={row.id}>
                    <strong>{row.kind}</strong>
                    <p>{row.body}</p>
                    <p className="muted">{formatDate(row.at)}</p>
                  </li>
                ))}
                {!reminders.length ? <li className="muted">No reminders yet.</li> : null}
              </ul>
            </section>
          </div>
        ) : null}

        {tab === "reports" ? (
          <div className="am-grid">
            <section className="desk-panel card">
              <h2>Revenue</h2>
              <p className="am-stat">
                MRR {money(revenue.mrr)} · ARR {money(revenue.arr)}
              </p>
              <p className="muted">Collected this month {money(revenue.collectedThisMonth)}</p>
              <BarList
                rows={revenue.byPlan.map((row) => ({ ...row, label: row.label, count: row.mrr, pretty: money(row.mrr) }))}
                valueKey="count"
              />
            </section>
            <section className="desk-panel card">
              <h2>User base</h2>
              <p className="am-stat">
                {users.active} active · {users.beta} beta · {users.paid} paid
              </p>
              <p className="muted">
                90-day growth {Math.round(users.growthRate * 100)}% · churn {Math.round(users.churnRate * 100)}%
              </p>
              <BarList rows={users.byRole.map((row) => ({ id: row.role, label: roleLabel(row.role), count: row.count }))} />
            </section>
            <section className="desk-panel card">
              <h2>Tax</h2>
              <p className="muted">Gross receipts by month and taxable totals by jurisdiction tag. For the CPA — not a filing.</p>
              <ul className="am-notes">
                {tax.periods.map((row) => (
                  <li key={row.period}>
                    {row.period} · {money(row.gross)} gross · {money(row.refunds)} refunds
                  </li>
                ))}
              </ul>
              <BarList
                rows={tax.byJurisdiction.map((row) => ({
                  id: row.jurisdiction,
                  label: row.jurisdiction,
                  count: row.amount,
                  pretty: money(row.amount),
                }))}
              />
            </section>
            <section className="desk-panel card">
              <h2>Corporate readiness</h2>
              <ul className="am-notes">
                {corporate.map((row) => (
                  <li key={row.org.id}>
                    <strong>{row.org.name}</strong>
                    <p>
                      {row.seats.members} seats · {row.seats.agents} agents · {money(row.collected)} billed
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          </div>
        ) : null}

        {tab === "forecast" ? (
          <div className="am-grid">
            <section className="desk-panel card am-wide">
              <h2>30 / 60 / 90</h2>
              <p className="muted">A simple line from recent sign-ups minus cancellations. Not a promise.</p>
              <Spark series={forecast.series} />
              <ul className="am-proj">
                {forecast.projections.map((row) => (
                  <li key={row.days}>
                    <strong>{row.days} days</strong>
                    <span>{row.accounts} desks</span>
                  </li>
                ))}
              </ul>
            </section>
            <section className="desk-panel card">
              <h2>By role</h2>
              <BarList rows={forecast.segments.role.map((row) => ({ ...row, label: roleLabel(row.id) }))} />
            </section>
            <section className="desk-panel card">
              <h2>By state</h2>
              <BarList rows={forecast.segments.state} />
            </section>
            <section className="desk-panel card">
              <h2>By county</h2>
              <BarList rows={forecast.segments.county} />
            </section>
            <section className="desk-panel card">
              <h2>By city</h2>
              <BarList rows={forecast.segments.city} />
            </section>
            <section className="desk-panel card">
              <h2>Recent desk notes</h2>
              <ul className="am-notes">
                {auditRows.map((row) => (
                  <li key={row.id}>
                    {row.action_type} · {formatDate(row.timestamp)}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        ) : null}
      </div>
    </AppLayout>
  );
}

function AccountDetail({
  bundle,
  orgs,
  session,
  refundNote,
  setRefundNote,
  credit,
  setCredit,
  methodRef,
  setMethodRef,
  onFlash,
  onBack,
}) {
  const { account, org, subscription, billing, invoices, payments, adjustments, reminders, seats } = bundle;
  const life = adminLifecycleSummary(account);
  const [tempPassword, setTempPassword] = useState("");
  const [deleteEmail, setDeleteEmail] = useState("");
  const [draft, setDraft] = useState({
    name: account.name,
    email: account.email,
    phone: account.phone,
    city: account.city,
    county: account.county,
    state: account.state,
  });
  const [bill, setBill] = useState({
    billing_name: billing?.billing_name || account.name,
    address_line: billing?.address_line || "",
    city: billing?.city || "",
    state: billing?.state || "",
    postal: billing?.postal || "",
    tax_id: billing?.tax_id || "",
    tax_jurisdiction: billing?.tax_jurisdiction || account.state,
  });

  return (
    <div className="am-stack">
      <p>
        <button type="button" className="text-btn" onClick={onBack}>
          All accounts
        </button>
      </p>
      <section className="desk-panel card">
        <h2>{account.name}</h2>
        <p className="muted">
          {roleLabel(account.role)} · {statusLabel(ACCOUNT_STATUSES, account.status)} · opened{" "}
          {formatDate(account.created_at)}
          {account.last_login ? ` · last visit ${formatDate(account.last_login)}` : " · no visit recorded"}
        </p>
        <form
          className="am-form am-form--wide"
          onSubmit={(event) => {
            event.preventDefault();
            try {
              captureRestorePoint(account.journey_user_id || account.id, {
                label: "Before account edit",
                actorId: session.userId,
                accountId: account.id,
                scope: RESTORE_SCOPES.account,
                source: "pre_change",
                force: true,
              });
              updateAccount(session, account.id, draft);
              onFlash("Account saved.");
            } catch (err) {
              onFlash(err.message);
            }
          }}
        >
          <label className="field">
            <span className="field-label">Name</span>
            <input className="field-input" value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Email</span>
            <input className="field-input" value={draft.email} onChange={(event) => setDraft({ ...draft, email: event.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Phone</span>
            <input className="field-input" value={draft.phone} onChange={(event) => setDraft({ ...draft, phone: event.target.value })} />
          </label>
          <div className="am-form__row">
            <label className="field">
              <span className="field-label">City</span>
              <input className="field-input" value={draft.city} onChange={(event) => setDraft({ ...draft, city: event.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">County</span>
              <input className="field-input" value={draft.county} onChange={(event) => setDraft({ ...draft, county: event.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">State</span>
              <input className="field-input" value={draft.state} onChange={(event) => setDraft({ ...draft, state: event.target.value })} />
            </label>
          </div>
          <button className="primary-btn" type="submit">
            Save details
          </button>
        </form>
        <div className="am-tools">
          <label className="field">
            <span className="field-label">Role</span>
            <select
              className="field-select"
              value={account.role}
              onChange={(event) => {
                changeAccountRole(session, account.id, event.target.value);
                onFlash("Role updated.");
              }}
            >
              {ACCOUNT_ROLES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Status</span>
            <select
              className="field-select"
              value={account.status}
              onChange={(event) => {
                const next = event.target.value;
                const lifeUserId = account.journey_user_id || account.id;
                if (next === "suspended" && account.status !== "suspended") {
                  adminSuspendDesk(session, account, "Admin desk status change");
                } else if (next === "active" && account.status === "suspended") {
                  adminReactivateDesk(session, account, "Admin desk status change");
                }
                setAccountStatus(session, account.id, next);
                onFlash("Status and lifecycle updated.");
              }}
            >
              {ACCOUNT_STATUSES.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span className="field-label">Organization</span>
            <select
              className="field-select"
              value={account.organization_id || ""}
              onChange={(event) => {
                assignAccountOrg(session, account.id, event.target.value);
                onFlash("Organization linked.");
              }}
            >
              <option value="">None</option>
              {orgs.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <div className="am-tools">
          {account.status === "suspended" ? (
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                adminReactivateDesk(session, account, "Manual override from Account Management.");
                overrideSuspension(session, account.id, "Manual override from Account Management.");
                onFlash("Suspension lifted and billing resumed.");
              }}
            >
              Override suspension
            </button>
          ) : (
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                adminSuspendDesk(session, account, "Admin desk suspension");
                setAccountStatus(session, account.id, "suspended");
                onFlash("Account suspended. Billing frozen.");
              }}
            >
              Suspend desk
            </button>
          )}
          <button
            type="button"
            className="ghost-btn"
            onClick={() => {
              adminReactivateDesk(session, account, "Admin reactivation");
              overrideSuspension(session, account.id, "Admin reactivation");
              setAccountStatus(session, account.id, "active");
              onFlash("Desk reactivated.");
            }}
          >
            Reactivate desk
          </button>
        </div>
        {org ? (
          <p className="muted">
            {org.name}
            {seats ? ` · ${seats.agents} agents${seats.limit ? ` / ${seats.limit} seats` : ""}` : ""}
          </p>
        ) : null}
      </section>

      <section className="desk-panel card">
        <h2>Subscription</h2>
        {subscription ? (
          <>
            <p>
              {planLabel(subscription.plan_id)} · {statusLabel(SUB_STATUSES, subscription.status)} · renews{" "}
              {formatDate(subscription.renewal_date)}
            </p>
            <p className="muted">Payment method {subscription.payment_method_ref}</p>
            {subscription.beta ? <p className="muted">Beta / trial flag is on.</p> : null}
            <div className="am-tools">
              <label className="field">
                <span className="field-label">Change plan</span>
                <select
                  className="field-select"
                  value={subscription.plan_id}
                  onChange={(event) => {
                    captureRestorePoint(account.journey_user_id || account.id, {
                      label: "Before plan change",
                      actorId: session.userId,
                      accountId: account.id,
                      scope: RESTORE_SCOPES.account,
                      source: "pre_change",
                      force: true,
                    });
                    changePlan(session, subscription.owner_id, event.target.value);
                    onFlash("Plan changed.");
                  }}
                >
                  {PLANS.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {planLabel(plan.id)}
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="button"
                className="primary-btn"
                onClick={() => {
                  const result = chargeOwner(session, subscription.owner_id, { force: "succeed", note: "Manual renewal" });
                  onFlash(result.skipped ? "No charge — this plan is comped." : "Charge recorded as paid.");
                }}
              >
                Charge / renew
              </button>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  chargeOwner(session, subscription.owner_id, { force: "fail", note: "Test decline" });
                  onFlash("Failed attempt logged, with a gentle reminder.");
                }}
              >
                Record a decline
              </button>
            </div>
            <form
              className="am-inline"
              onSubmit={(event) => {
                event.preventDefault();
                setPaymentMethod(session, subscription.owner_id, methodRef);
                setMethodRef("");
                onFlash("Payment method reference saved.");
              }}
            >
              <input
                className="field-input"
                value={methodRef}
                onChange={(event) => setMethodRef(event.target.value)}
                placeholder="Gateway reference (pm_… )"
              />
              <button className="ghost-btn" type="submit">
                Store method
              </button>
            </form>
            <form
              className="am-inline"
              onSubmit={(event) => {
                event.preventDefault();
                try {
                  issueCredit(session, subscription.owner_id, { amount: credit.amount, note: credit.note });
                  setCredit({ amount: "", note: "" });
                  onFlash("Credit posted.");
                } catch (err) {
                  onFlash(err.message);
                }
              }}
            >
              <input
                className="field-input"
                value={credit.amount}
                onChange={(event) => setCredit({ ...credit, amount: event.target.value })}
                placeholder="Credit amount"
              />
              <input
                className="field-input"
                value={credit.note}
                onChange={(event) => setCredit({ ...credit, note: event.target.value })}
                placeholder="Note"
                required
              />
              <button className="ghost-btn" type="submit">
                Issue credit
              </button>
              <button
                type="button"
                className="ghost-btn"
                onClick={() => {
                  issueCredit(session, subscription.owner_id, { compMonth: true, note: credit.note || "One month comped." });
                  onFlash("A month was comped. Renewal moved forward.");
                }}
              >
                Comp a month
              </button>
            </form>
          </>
        ) : (
          <p className="muted">No subscription on this desk yet.</p>
        )}
      </section>

      <section className="desk-panel card">
        <h2>Lifecycle</h2>
        <p>
          Desk lifecycle: {lifeStatusLabel(life.status)}
          {life.suspensionUntil ? ` · suspension window through ${formatDate(life.suspensionUntil)}` : ""}
        </p>
        {life.reason ? <p className="muted">Note: {life.reason}</p> : null}
        {subscription?.billing_frozen ? (
          <p className="confirm-banner">Billing frozen · anchor {formatDate(subscription.billing_anchor_date)}</p>
        ) : null}
      </section>

      <section className="desk-panel card">
        <h2>Emergency password reset</h2>
        <p className="muted">Local prototype only — sets a temporary password and flags rotation. Nothing is emailed.</p>
        <button
          type="button"
          className="primary-btn"
          onClick={() => {
            try {
              const result = emergencyPasswordResetForAccount(session, account);
              setTempPassword(result.temporary_password);
              onFlash("Temporary password issued. Copy it now — it is not emailed.");
            } catch (err) {
              onFlash(err.message);
            }
          }}
        >
          Issue emergency reset
        </button>
        {tempPassword ? (
          <p className="confirm-banner">
            Temporary password: <code>{tempPassword}</code>
          </p>
        ) : null}
      </section>

      <RestoreTimeline
        session={session}
        userId={account.journey_user_id || account.id}
        adminMode
        title="Account restore timeline"
        lead="Continuous saves plus admin snapshots for this account on this device. Restore requires typing RESTORE."
        onFlash={onFlash}
      />

      <section className="desk-panel card">
        <h2>Delete account</h2>
        <p className="muted">
          Soft-deletes this desk record on the device, cancels billing, and keeps a restore point. Buyer/seller
          checklist data on client devices is not removed from those devices.
        </p>
        <form
          className="am-inline"
          onSubmit={(event) => {
            event.preventDefault();
            try {
              captureRestorePoint(account.journey_user_id || account.id, {
                label: "Before account deletion",
                actorId: session.userId,
                accountId: account.id,
                scope: RESTORE_SCOPES.account,
                source: "pre_change",
                force: true,
              });
              deleteAccount(session, account.id, { confirmEmail: deleteEmail });
              onFlash("Account deleted on this device.");
              onBack();
            } catch (err) {
              onFlash(err.message);
            }
          }}
        >
          <input
            className="field-input"
            value={deleteEmail}
            onChange={(event) => setDeleteEmail(event.target.value)}
            placeholder={`Type ${account.email} to confirm`}
            required
          />
          <button type="submit" className="ghost-btn">
            Delete account
          </button>
        </form>
      </section>

      <section className="desk-panel card">
        <h2>Billing profile</h2>
        <form
          className="am-form am-form--wide"
          onSubmit={(event) => {
            event.preventDefault();
            updateBillingProfile(session, billing?.owner_id || account.id, bill);
            onFlash("Billing profile saved.");
          }}
        >
          <label className="field">
            <span className="field-label">Billing name</span>
            <input className="field-input" value={bill.billing_name} onChange={(event) => setBill({ ...bill, billing_name: event.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Address</span>
            <input className="field-input" value={bill.address_line} onChange={(event) => setBill({ ...bill, address_line: event.target.value })} />
          </label>
          <div className="am-form__row">
            <label className="field">
              <span className="field-label">City</span>
              <input className="field-input" value={bill.city} onChange={(event) => setBill({ ...bill, city: event.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">State</span>
              <input className="field-input" value={bill.state} onChange={(event) => setBill({ ...bill, state: event.target.value })} />
            </label>
            <label className="field">
              <span className="field-label">Postal</span>
              <input className="field-input" value={bill.postal} onChange={(event) => setBill({ ...bill, postal: event.target.value })} />
            </label>
          </div>
          <label className="field">
            <span className="field-label">Tax ID (optional)</span>
            <input className="field-input" value={bill.tax_id} onChange={(event) => setBill({ ...bill, tax_id: event.target.value })} />
          </label>
          <label className="field">
            <span className="field-label">Tax jurisdiction</span>
            <input
              className="field-input"
              value={bill.tax_jurisdiction}
              onChange={(event) => setBill({ ...bill, tax_jurisdiction: event.target.value })}
            />
          </label>
          <button className="primary-btn" type="submit">
            Save billing
          </button>
        </form>
      </section>

      <section className="desk-panel card">
        <h2>Invoices and receipts</h2>
        <table className="admin-table am-table">
          <thead>
            <tr>
              <th>Invoice</th>
              <th>Status</th>
              <th>Amount</th>
              <th>Issued</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((row) => (
              <tr key={row.id}>
                <td>{row.number}</td>
                <td>{row.status}</td>
                <td>{money(row.amount)}</td>
                <td>{formatDate(row.issued_at)}</td>
              </tr>
            ))}
            {!invoices.length ? (
              <tr>
                <td colSpan={4} className="muted">
                  No invoices yet.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      <section className="desk-panel card">
        <h2>Payment history</h2>
        <ul className="am-notes">
          {payments.map((row) => (
            <li key={row.id}>
              {formatDate(row.at)} · {row.result} · {money(row.amount)} · {row.note || "—"}
              {row.result === "succeeded" ? (
                <form
                  className="am-inline"
                  onSubmit={(event) => {
                    event.preventDefault();
                    try {
                      markRefund(session, row.id, refundNote);
                      setRefundNote("");
                      onFlash("Refund marked.");
                    } catch (err) {
                      onFlash(err.message);
                    }
                  }}
                >
                  <input
                    className="field-input"
                    value={refundNote}
                    onChange={(event) => setRefundNote(event.target.value)}
                    placeholder="Refund note"
                    required
                  />
                  <button className="text-btn" type="submit">
                    Mark refund
                  </button>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
        {adjustments.length ? (
          <>
            <h3>Credits</h3>
            <ul className="am-notes">
              {adjustments.map((row) => (
                <li key={row.id}>
                  {row.kind} · {money(row.amount)} · {row.note}
                </li>
              ))}
            </ul>
          </>
        ) : null}
        {reminders.length ? (
          <>
            <h3>Reminders</h3>
            <ul className="am-notes">
              {reminders.map((row) => (
                <li key={row.id}>{row.body}</li>
              ))}
            </ul>
          </>
        ) : null}
        {account.role === "agent" || account.role === "broker" ? (
          <p>
            <Link className="text-btn" to="/admin/users">
              Open in Users
            </Link>
          </p>
        ) : null}
      </section>
    </div>
  );
}
